import { PaymentPane, sortByDateKey } from '@billy/design-system'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useConnectBankBanner } from '@features/bankAccounts/hooks/useConnectBankBanner'

import { CustomEvent } from '../../../../enums/customEvent'
import { useCustomEventListener } from '../../../../hooks/useCustomEventListener'
import { useInvoice } from '../../hooks/useInvoice'
import { InvoicePaymentPaneItem } from './elements/InvoicePaymentPaneItem'
import { getInvoicePaymentPaneData } from './utils/getInvoicePaymentPaneData'

interface InvoicePaymentPaneProps {
  invoiceId: string
}

export const InvoicePaymentPane = ({ invoiceId }: InvoicePaymentPaneProps): ReactElement => {
  const { data: invoiceData, isLoading, refetch } = useInvoice(invoiceId)
  const { t } = useTranslation()
  const connectBankBanner = useConnectBankBanner()

  const invoiceDataSorted = useMemo(() => {
    return invoiceData?.invoice
      ? {
          ...invoiceData.invoice,
          balanceModifiers: sortByDateKey(invoiceData.invoice.balanceModifiers || [], 'entryDate'),
        }
      : undefined
  }, [invoiceData?.invoice])

  const invoicePaymentPaneData = useMemo(() => {
    return getInvoicePaymentPaneData(t, invoiceDataSorted, isLoading, undefined, connectBankBanner)
  }, [t, invoiceDataSorted, isLoading, connectBankBanner])

  useCustomEventListener(CustomEvent.InvoiceUpdated, () => refetch())

  return (
    <PaymentPane {...invoicePaymentPaneData}>
      {invoiceDataSorted?.balanceModifiers.map(({ id, ...rest }) => (
        <InvoicePaymentPaneItem key={id} id={id} invoiceType={invoiceDataSorted.type} {...rest} />
      ))}
    </PaymentPane>
  )
}
